import React, { ReactNode, useMemo, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, LucideIcon } from 'lucide-react';

// Optimized scrollbar styles (injected once)
if (typeof document !== 'undefined') {
  const styleId = 'form-modal-scrollbar-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Optimized scrollbar for FormModal - no transitions for better performance */
      .form-modal-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .form-modal-scrollbar::-webkit-scrollbar-track {
        background: var(--color-bg-primary);
        border-radius: 4px;
      }
      .form-modal-scrollbar::-webkit-scrollbar-thumb {
        background: var(--color-border);
        border-radius: 4px;
        /* No transition for better scroll performance */
      }
      .form-modal-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--color-text-muted);
      }
    `;
    document.head.appendChild(style);
  }
}

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  children: ReactNode;
}

export const FormModal: React.FC<FormModalProps> = memo(({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  maxWidth = 'md',
  children
}) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [isOpen]);

  const maxWidthClass = useMemo(() => ({
    'sm': 'max-w-sm',
    'md': 'max-w-md',
    'lg': 'max-w-lg',
    'xl': 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl'
  }[maxWidth]), [maxWidth]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
      style={{ 
        willChange: 'opacity',
        transform: 'translateZ(0)',
        zIndex: 99999,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className={`rounded-2xl shadow-2xl ${maxWidthClass} w-full max-h-[95vh] overflow-y-auto border relative form-modal-scrollbar`}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)',
          willChange: 'scroll-position',
          transform: 'translateZ(0)', 
          contain: 'layout style paint',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--color-border) var(--color-bg-primary)'
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="sticky top-0 rounded-t-2xl p-6 pb-4 z-[70]"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)',
            willChange: 'transform',
            transform: 'translateZ(0)' // Force GPU acceleration for sticky
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {Icon && (
                <div 
                  className="p-2 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)'
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  {title}
                </h3>
                {subtitle && (
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors duration-200"
              style={{
                color: 'var(--color-text-muted)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-muted)';
              }}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div 
          className="p-8"
          style={{
            contain: 'layout style',
            transform: 'translateZ(0)'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal in a portal to document.body to ensure it's above everything
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : null;
});

FormModal.displayName = 'FormModal';

